import type { ReactNode } from 'react'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { goBack, Link } from '../../../router.js'
import { fmtDateHeure, type Lecture } from '../../api.js'
import {
  LIBELLE_ETAT, LIBELLE_GRAVITE, Pastille, PastilleEtat, TON_ETAT, TON_GRAVITE, classeTon, fmtAge, renommerSalles,
} from '../commun.js'
import {
  LIBELLE_CRITICITE, cheminAppareil, cheminIncident, fmtQuand, libelleType, segmentsEtats, tonStatutIncident,
  type AppareilBref, type Comptes, type IncidentBref,
} from './modele.js'
import { useMenuSonde } from '../menu-sonde.js'
import './fiches.css'

/** « ← Retour » : un vrai retour d'historique ; le repli est le parent logique. */
export function Retour({ repli, libelle = 'Retour' }: { repli: string; libelle?: string }) {
  return (
    <button type="button" className="lc-crumb" onClick={() => goBack(repli)}>
      <ArrowLeft size={14} aria-hidden="true" /> {libelle}
    </button>
  )
}

/** Une heure de Paris, la date complète au survol. */
export function Quand({ iso }: { iso: string | null | undefined }) {
  if (!iso) return <span>—</span>
  return <time dateTime={iso} title={fmtDateHeure(iso)}>{fmtQuand(iso)}</time>
}

/**
 * Ce que la page peut montrer d'une lecture : la donnée fraîche, ou la
 * dernière bonne GRISÉE pendant une panne passagère, ou rien.
 */
export function donneeDe<T>(lecture: Lecture<T> | null, precedente: T | null): { data: T | null; perime: boolean } {
  if (lecture?.etat === 'ok') return { data: lecture.data, perime: false }
  return { data: precedente, perime: precedente !== null && lecture !== null }
}

/** Vide honnête : une phrase, ce qu'il faut faire, jamais une illustration. */
export function Vide({ children }: { children: ReactNode }) {
  return <div className="sv-fiche-vide">{children}</div>
}

/** Squelette fixe à la forme du contenu, sans miroitement. */
export function Squelette({ lignes = 3 }: { lignes?: number }) {
  return (
    <div className="sv-fiche-squelette" aria-busy="true" aria-label="Chargement">
      {Array.from({ length: lignes }, (_, i) => <span key={i} />)}
    </div>
  )
}

/** Panneau de section : titre, indice, corps. */
export function Section({ titre, indice, id, children, actions }: { titre: string; indice?: ReactNode; id?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="lc-panel sv-fiche-section" aria-labelledby={id}>
      <header className="lc-panel__head sv-fiche-section__tete">
        <h2 className="lc-section-title" id={id}>{titre}</h2>
        {indice ? <span className="lc-hint">{indice}</span> : null}
        {actions ? <span className="sv-fiche-section__actions">{actions}</span> : null}
      </header>
      <div className="lc-panel__body">{children}</div>
    </section>
  )
}

/**
 * Barre des états d'un ensemble d'appareils, en couleurs : UN code couleur
 * pour tout le site (commun.tsx). Les éteints comme prévu sont neutres,
 * jamais rouges ; les inconnus et périmés gris.
 */
export function BarreEtats({ comptes, legende = true }: { comptes: Comptes; legende?: boolean }) {
  const segs = segmentsEtats(comptes)
  if (comptes.total === 0 || segs.length === 0) return <div className="sv-fiche-barre sv-fiche-barre--vide" aria-hidden="true" />
  return (
    <div className="sv-fiche-barre-bloc">
      <div className="sv-fiche-barre" role="img"
           aria-label={segs.map((s) => `${s.n} ${(LIBELLE_ETAT[s.etat] ?? s.etat).toLowerCase()}`).join(', ')}>
        {segs.map((s) => (
          <span key={s.etat} className={`sv-fiche-barre__seg sv-fiche-ton-fond--${TON_ETAT[s.etat] ?? 'gris'}`}
                style={{ flexGrow: s.n }} title={`${s.n} · ${LIBELLE_ETAT[s.etat] ?? s.etat}`} />
        ))}
      </div>
      {legende ? (
        <ul className="sv-fiche-legende">
          {segs.map((s) => (
            <li key={s.etat}><Pastille ton={TON_ETAT[s.etat] ?? 'gris'}>{s.n} {(LIBELLE_ETAT[s.etat] ?? s.etat).toLowerCase()}</Pastille></li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** Une ligne d'appareil cliquable : nom, type, état, cause, âge de la donnée. */
export function LigneAppareil({ a, extra }: { a: AppareilBref; extra?: ReactNode }) {
  const s = a.state
  const cause = s.cause && s.cause !== 'SELF' && s.cause_label ? s.cause_label : null
  const menu = useMenuSonde()
  return (
    <li>
      <Link className={`sv-fiche-app sv-fiche-app--${TON_ETAT[s.display_state] ?? 'gris'}`} href={cheminAppareil(a.uid)}
            {...menu.appareil({ uid: a.uid, nom: a.display_name, zone_code: a.zone_code })}>
        <span className="sv-fiche-app__corps">
          <span className="sv-fiche-app__nom">{a.display_name}</span>
          <span className="sv-fiche-app__meta">
            {libelleType(a.kind)}
            {a.zone_detail ? ` · ${a.zone_detail}` : ''}
            {a.criticality === 'critical_evening' ? ` · ${LIBELLE_CRITICITE['critical_evening']}` : ''}
            {cause ? ` · ${cause}` : ''}
            {s.flapping ? ' · instable' : ''}
            {extra ? <> · {extra}</> : null}
          </span>
        </span>
        <span className="sv-fiche-app__etat">
          <PastilleEtat etat={s.display_state} libelle={s.display_label} />
          <span className="sv-fiche-app__age">{s.since ? <>depuis <Quand iso={s.since} /></> : fmtAge(s.data_age_s)}</span>
        </span>
        <ChevronRight size={16} aria-hidden="true" className="sv-fiche-app__fleche" />
      </Link>
    </li>
  )
}

/** Incidents brefs (fiche salle, fiche appareil). */
export function IncidentsBrefs({ items, vide }: { items: IncidentBref[]; vide: string }) {
  const menu = useMenuSonde()
  if (items.length === 0) return <Vide><span>{vide}</span></Vide>
  return (
    <ul className="sv-fiche-liste">
      {items.map((i) => (
        <li key={i.id}>
          <Link className="sv-fiche-inc" href={cheminIncident(i.id)}
                {...menu.incident({ id: i.id, titre: renommerSalles(i.title), status: i.status, acquitte: i.acked })}>
            <Pastille ton={TON_GRAVITE[i.severity] ?? 'gris'}>{LIBELLE_GRAVITE[i.severity] ?? i.severity}</Pastille>
            <span className="sv-fiche-inc__corps">
              <span className="sv-fiche-inc__titre">{renommerSalles(i.title)}</span>
              <span className="sv-fiche-inc__meta">
                Ouvert <Quand iso={i.opened_at} />
                {i.affected_count > 1 ? ` · ${i.affected_count} appareils touchés` : ''}
                {i.acked ? ' · pris en charge' : ''}
              </span>
            </span>
            <span className={classeTon(tonStatutIncident(i.status, i.severity))}>{LIBELLE_STATUT[i.status] ?? i.status}</span>
            <ChevronRight size={16} aria-hidden="true" className="sv-fiche-app__fleche" />
          </Link>
        </li>
      ))}
    </ul>
  )
}

export const LIBELLE_STATUT: Record<string, string> = {
  open: 'Ouvert', acknowledged: 'Pris en charge', recovering: 'En rétablissement', resolved: 'Résolu',
}

/** Fait d'un en-tête : libellé, valeur, et sa source ou son âge en dessous. */
export function Fait({ libelle, children, sous }: { libelle: string; children: ReactNode; sous?: ReactNode }) {
  return (
    <div className="sv-fiche-fait">
      <dt>{libelle}</dt>
      <dd>{children}{sous ? <span className="sv-fiche-fait__sous">{sous}</span> : null}</dd>
    </div>
  )
}
